import type { ReactNode } from "react";
import type { TranslateFn } from "@/lib/i18n/intl";
import { TRIOTY_MARK_DATA_URL } from "./triotyMark";
import { captainFirstName } from "@/modules/pos/lib/captain";

/**
 * THE CUSTOMER PAPER'S LAYOUT (owner, 2026-10-08) — one set of blocks for every
 * slip a guest is handed: the pre-bill check, the browser receipt and the
 * receipt the print computer draws for a receipt printer. The owner's
 * reference is an Egyptian restaurant POS slip, and the brief is to match it
 * line for line:
 *
 *   PIERO                                   (store name; no logo yet)
 *   Order No   : 6030          Table  5
 *   Check In   : 10/08/2026 10:45 AM
 *   Check Out  :
 *   - - - - - - - - - - - - - - - - - -
 *   Customer   : General Customer
 *   Captain    : Mohamed Hamed
 *   Guest No   : 0
 *   Product            Qty   Price   Total
 *   ──────────────────────────────────────
 *   Cappuccino          1     90     90.00
 *   ──────────────────────────────────────
 *   Discount 0 %
 *   Service 12 % :   67.2
 *   VAT 14% :        87.81
 *   Total : 715.01 L.E              Open
 *   ──────────────────────────────────────
 *   footer
 *   ┌ Powered by … ┐
 *
 * Inline styles only, because the print computer serialises this markup into a
 * standalone document and rasterises it (lib/printing/raster.ts): a class the
 * app's stylesheet defines would not travel with it. Nothing here may be wider
 * than the paper: the rasteriser scales the BODY's width to the print head, so
 * an over-wide line is cut off at the edge rather than wrapped.
 */

/** The Total column: always two decimals, no currency (four columns do not fit 72 mm). */
export function paperNumber(value: number): string {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/** The Price column and the charge rows: "90", "67.2", "87.81" — as the reference prints them. */
export function paperAmount(value: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
}

/**
 * "08/10/2026 06:43 PM" in the STORE's zone, Western digits, on an Arabic and
 * an English slip alike. A localized Arabic stamp mixes an Arabic period word
 * into the digits, and inside an RTL slip its pieces reorder ("ص8، 2026/10/").
 */
export function paperStamp(instant: string, timezone: string | null | undefined): string {
  const at = new Date(instant);
  if (Number.isNaN(at.getTime())) return instant;
  const build = (zone?: string) => {
    const parts = new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: zone,
    }).formatToParts(at);
    const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((entry) => entry.type === type)?.value ?? "";
    return `${part("day")}/${part("month")}/${part("year")} ${part("hour")}:${part("minute")} ${part("dayPeriod").toUpperCase()}`;
  };
  if (timezone) {
    try {
      return build(timezone);
    } catch {
      // An unknown zone throws; the device's zone beats no time at all.
    }
  }
  return build();
}

export type PaperFact = {
  key: string;
  label: string;
  value: ReactNode;
  /** Digits, times and phone numbers: isolated LTR inside an Arabic slip. */
  ltr?: boolean;
  /** The reference's dotted rule between the bill's times and its people. */
  ruleBefore?: boolean;
};

/**
 * The head's first line (owner, 2026-10-08): the table and the order number
 * together, in the same style as every other fact — "Table / Order No :
 * 5 / 6303". A bill at no table prints "Order No : 6303".
 */
export function orderFact(t: TranslateFn, dailySequence: number, table: string | null): PaperFact {
  return table
    ? {
        key: "order",
        label: `${t((m) => m.paper.table)} / ${t((m) => m.paper.orderNo)}`,
        value: `${table} / ${dailySequence}`,
      }
    : { key: "order", label: t((m) => m.paper.orderNo), value: String(dailySequence), ltr: true };
}

/**
 * The people line — «الكابتن», when the bill names one — opened by the
 * reference's dotted rule. No Guest No (owner, 2026-10-08: "not needed");
 * nothing at all when nobody is named.
 *
 * D153: the name is the bill's CAPTAIN (the waiter who owns the table), never
 * the staff member who opened it — under the cashier-keyed pad that was the
 * cashier, printed under a word claiming the opposite. The FIRST name, which
 * is what the guest asks for.
 */
export function peopleFacts(t: TranslateFn, captainName: string | null): PaperFact[] {
  return captainName
    ? [
        {
          key: "captain",
          label: t((m) => m.paper.captain),
          value: captainFirstName(captainName),
          ruleBefore: true,
        },
      ]
    : [];
}

const RULE = "1.5px solid #000";
const DOTTED = "1px dashed #000";

export function PaperHead({
  storeName,
  logoUrl = null,
  place,
  facts,
}: {
  storeName: string | null;
  /**
   * The store's logo, at the head's start with the name beside it, as on the
   * reference. On the print computer it must be a data URL: the slip is drawn
   * inside an SVG image, which loads nothing from the network.
   */
  logoUrl?: string | null;
  /** "Table 5", printed at the end of the FIRST fact's line; null off the floor. */
  place: { label: string; value: string } | null;
  facts: PaperFact[];
}) {
  const cell = { verticalAlign: "top", paddingTop: "0.3mm", paddingBottom: "0.3mm" } as const;
  return (
    <div data-print-head="">
      {logoUrl ? (
        <div style={{ display: "flex", alignItems: "center", gap: "4mm", marginBottom: "2.5mm" }}>
          <img
            src={logoUrl}
            alt=""
            style={{ width: "22mm", height: "22mm", objectFit: "contain", flexShrink: 0 }}
            data-print-logo=""
          />
          {storeName ? (
            <div style={{ fontSize: "20px", fontWeight: 900, lineHeight: 1.2 }} data-print-store="">
              {storeName}
            </div>
          ) : null}
        </div>
      ) : storeName ? (
        <div
          style={{ textAlign: "center", fontSize: "20px", fontWeight: 900, lineHeight: 1.2, marginBottom: "2.5mm" }}
          data-print-store=""
        >
          {storeName}
        </div>
      ) : null}
      {/* `auto`, not the standalone document's `table-layout: fixed`: the label
          and colon columns are sized to their text, which fixed layout squeezes
          to nothing so the labels print over the values. */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "11px", tableLayout: "auto" }}>
        <tbody>
          {facts.map((fact, index) => {
            const rule = fact.ruleBefore ? { borderTop: DOTTED, paddingTop: "1mm" } : {};
            return (
              <tr key={fact.key} data-print-fact={fact.key}>
                <td style={{ ...cell, ...rule, fontWeight: 700, whiteSpace: "nowrap", width: "1%", paddingInlineEnd: "1.5mm" }}>
                  {fact.label}
                </td>
                <td style={{ ...cell, ...rule, width: "1%", paddingInlineEnd: "2mm" }}>:</td>
                <td style={{ ...cell, ...rule }}>
                  <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "2mm" }}>
                    <span style={{ whiteSpace: "nowrap" }}>
                      {fact.ltr ? <bdi dir="ltr">{fact.value}</bdi> : fact.value}
                    </span>
                    {index === 0 && place ? (
                      <span style={{ fontSize: "15px", fontWeight: 800, textAlign: "end" }} data-print-place="">
                        {place.label}&nbsp;&nbsp;&nbsp;{place.value}
                      </span>
                    ) : null}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export type PaperItem = {
  key: string;
  name: string;
  /** Sub-lines under the name: the combo, the swap, the extras. */
  details: { text: string; ltr?: boolean }[];
  quantity: number;
  unitPrice: number;
  total: number;
  /** A voided plate stays on the paper, struck through. */
  struck?: boolean;
};

export function PaperItems({ items, t }: { items: PaperItem[]; t: TranslateFn }) {
  const head = { fontWeight: 800, fontSize: "11px", paddingBottom: "0.8mm", borderBottom: RULE } as const;
  const figure = { verticalAlign: "top", whiteSpace: "nowrap", paddingInlineStart: "1.5mm" } as const;
  return (
    <table
      style={{ width: "100%", borderCollapse: "collapse", margin: "2mm 0 1.5mm", borderBottom: RULE, fontSize: "11px" }}
      data-print-items=""
    >
      <thead>
        <tr>
          <th style={{ ...head, textAlign: "start" }}>{t((m) => m.paper.product)}</th>
          <th style={{ ...head, textAlign: "center", paddingInlineStart: "1.5mm", width: "10%" }}>{t((m) => m.paper.quantity)}</th>
          <th style={{ ...head, textAlign: "center", paddingInlineStart: "1.5mm", width: "16%" }}>{t((m) => m.paper.price)}</th>
          <th style={{ ...head, textAlign: "end", paddingInlineStart: "1.5mm", width: "22%" }}>{t((m) => m.paper.lineTotal)}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => {
          const pad = { paddingTop: index === 0 ? "1mm" : "0.5mm", paddingBottom: "0.5mm" };
          return (
            <tr key={item.key} data-print-item="" style={{ textDecoration: item.struck ? "line-through" : undefined }}>
              <td style={{ verticalAlign: "top", ...pad }}>
                <div>{item.name}</div>
                {item.details.map((detail, detailIndex) => (
                  <div key={detailIndex} dir={detail.ltr ? "ltr" : undefined} style={{ fontSize: "9px" }}>
                    {detail.text}
                  </div>
                ))}
              </td>
              <td style={{ ...figure, ...pad, textAlign: "center" }}>{item.quantity}</td>
              <td style={{ ...figure, ...pad, textAlign: "center" }}>{paperAmount(item.unitPrice)}</td>
              <td style={{ ...figure, ...pad, textAlign: "end" }}>{paperNumber(item.total)}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/**
 * One charge row under the items — "Service 12 % : 67.2". The figure sits in
 * the middle of the paper, after the label's colon, as on the reference; not
 * pushed to the far edge where the Total column is.
 */
export function PaperSumRow({
  label,
  value,
  small,
  testId,
}: {
  label: ReactNode;
  value: ReactNode;
  small?: boolean;
  testId?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        gap: "1.5mm",
        fontSize: small ? "9px" : "11px",
        paddingInlineStart: small ? "3mm" : undefined,
      }}
      data-testid={testId}
    >
      <span style={{ width: "52%", flexShrink: 0 }}>{label}</span>
      <span>:</span>
      <span style={{ whiteSpace: "nowrap" }}>
        <bdi dir="ltr">{value}</bdi>
      </span>
    </div>
  );
}

/** THE TOTAL: "Total : 715.01 L.E", large and underlined, the bill's state at the end. */
export function PaperTotal({
  label,
  value,
  status,
  statusTestId,
}: {
  label: string;
  value: string;
  status: string | null;
  statusTestId?: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        justifyContent: "space-between",
        gap: "2mm",
        margin: "3mm 0 1mm",
        paddingBottom: "1mm",
        borderBottom: RULE,
      }}
      data-print-total=""
    >
      <span style={{ fontSize: "18px", fontWeight: 900, textDecoration: "underline" }}>
        {label} : <span>{value}</span>
      </span>
      {status ? (
        <span
          style={{ fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap" }}
          data-print-status=""
          data-testid={statusTestId}
        >
          {status}
        </span>
      ) : null}
    </div>
  );
}

/** The store's own line, then the reference's boxed "Powered by" credit. */
export function PaperFooter({
  text,
  poweredBy,
  testId,
}: {
  text: string | null;
  poweredBy: string;
  testId?: string;
}) {
  return (
    <>
      {text ? (
        <div style={{ marginTop: "3mm", fontSize: "12px", fontStyle: "italic" }} data-testid={testId}>
          {text}
        </div>
      ) : null}
      <div
        style={{
          margin: "3mm 2mm 0",
          padding: "1.5mm",
          border: DOTTED,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "2.5mm",
          fontSize: "10px",
        }}
        data-print-powered-by=""
      >
        <img src={TRIOTY_MARK_DATA_URL} alt="" style={{ width: "8mm", height: "8mm", flexShrink: 0 }} data-print-trioty-mark="" />
        <div style={{ textAlign: "center" }}>
          <div style={{ fontWeight: 700 }}>{poweredBy}</div>
          <div dir="ltr">trioty.com</div>
        </div>
      </div>
    </>
  );
}
